import React, { useState } from "react";
import {
  FolderGit2, Plus, Pencil, Trash2, Github, ExternalLink, Youtube,
  Star, Loader2,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, AppModal,
  Callout, Checkbox, ConfirmModal, EmptyState, FormField, IconButton,
  Input, Textarea,
} from "../ui";
import NavChip from "./NavChip";
import { dateRange } from "./constants";
import { projectsApi, uploadFile, errorText } from "./portfolioApi";

/**
 * ProjectsSection — what the student built, with the proof attached.
 *
 * ⚠️ A repo link, a live URL and a demo video are THREE different proofs and a
 * project may carry any combination. A data pipeline has a repo and no demo; a
 * design system has a live site and no repo; a hackathon build is very often
 * only a video. Requiring one of them, or collapsing them into a single "link"
 * field, loses whichever the student actually has.
 */

const EMPTY = {
  title: "", description: "", role: "", techStack: "",
  repoUrl: "", liveUrl: "", videoUrl: "", highlights: "",
  startDate: "", endDate: "", ongoing: false, coverImage: "", featured: false,
};

const toForm = (p) => ({
  title: p.title || "",
  description: p.description || "",
  role: p.role || "",
  techStack: (p.techStack || []).join(", "),
  repoUrl: p.repoUrl || "",
  liveUrl: p.liveUrl || "",
  videoUrl: p.videoUrl || "",
  highlights: (p.highlights || []).join("\n"),
  startDate: p.startDate ? p.startDate.slice(0, 10) : "",
  endDate: p.endDate ? p.endDate.slice(0, 10) : "",
  ongoing: !!p.ongoing,
  coverImage: p.coverImage || "",
  featured: !!p.featured,
});

const toPayload = (f) => ({
  ...f,
  techStack: f.techStack.split(",").map((s) => s.trim()).filter(Boolean),
  highlights: f.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
  endDate: f.ongoing ? "" : f.endDate,
});

const ProjectsSection = ({ projects = [], onPortfolioChange, readOnly = false }) => {
  const [modal, setModal] = useState(null);      // { mode: 'add'|'edit', project? }
  const [confirm, setConfirm] = useState(null);  // project pending delete
  const [busy, setBusy] = useState(false);

  const sorted = [...projects].sort((a, b) => {
    if (a.featured !== b.featured) return a.featured ? -1 : 1;
    return (a.order || 0) - (b.order || 0);
  });

  const del = async () => {
    setBusy(true);
    try {
      const { portfolio } = await projectsApi.remove(confirm._id);
      onPortfolioChange(portfolio, "Project removed.");
      setConfirm(null);
    } catch (err) {
      onPortfolioChange(null, errorText(err, "Failed to remove the project."), "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppCard>
      <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <AppCardTitle className="[&_svg]:tw-text-primary">
            <FolderGit2 size={18} /> Projects
            {projects.length > 0 && (
              <span className="tw-ml-1 tw-text-sm tw-font-normal tw-text-content-subtle">
                ({projects.length})
              </span>
            )}
          </AppCardTitle>
          <p className="tw-m-0 tw-mt-1 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">
            What you have built, with the code, the demo or the video that proves it.
          </p>
        </div>
        {!readOnly && (
          <AppButton variant="outline" size="sm" onClick={() => setModal({ mode: "add" })}>
            <Plus size={14} /> Add project
          </AppButton>
        )}
      </AppCardHeader>

      <AppCardContent>
        {sorted.length === 0 ? (
          <EmptyState
            icon={FolderGit2}
            title="No projects yet"
            description={readOnly
              ? "This student has not added any projects."
              : "Add the things you have built. A repository link is worth more than a paragraph."}
            action={!readOnly && (
              <AppButton size="sm" onClick={() => setModal({ mode: "add" })}>
                <Plus size={14} /> Add your first project
              </AppButton>
            )}
          />
        ) : (
          <ul className="tw-m-0 tw-grid tw-list-none tw-gap-3 tw-p-0 min-[900px]:tw-grid-cols-2">
            {sorted.map((p) => (
              <ProjectCard
                key={p._id}
                project={p}
                readOnly={readOnly}
                onEdit={() => setModal({ mode: "edit", project: p })}
                onDelete={() => setConfirm(p)}
              />
            ))}
          </ul>
        )}
      </AppCardContent>

      {modal && (
        <ProjectModal
          mode={modal.mode}
          project={modal.project}
          onClose={() => setModal(null)}
          onSaved={(portfolio, message) => { onPortfolioChange(portfolio, message); setModal(null); }}
        />
      )}

      <ConfirmModal
        show={!!confirm}
        onHide={() => !busy && setConfirm(null)}
        onConfirm={del}
        loading={busy}
        variant="danger"
        title="Remove this project?"
        message={confirm ? `"${confirm.title}" will be removed from your portfolio.` : ""}
        confirmText="Remove"
      />
    </AppCard>
  );
};

/* ── One project ──────────────────────────────────────────────────────────── */

const ProjectCard = ({ project: p, readOnly, onEdit, onDelete }) => {
  const range = dateRange(p.startDate, p.endDate, p.ongoing);
  const links = [
    p.repoUrl && { href: p.repoUrl, icon: Github, label: "Code" },
    p.liveUrl && { href: p.liveUrl, icon: ExternalLink, label: "Live" },
    p.videoUrl && { href: p.videoUrl, icon: Youtube, label: "Video" },
  ].filter(Boolean);

  return (
    <li className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-transition-colors hover:tw-border-primary/40">
      {p.coverImageUrl && (
        <img
          src={p.coverImageUrl}
          alt=""
          className="tw-h-32 tw-w-full tw-object-cover"
          loading="lazy"
        />
      )}
      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2.5 tw-p-4">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
          <div className="tw-min-w-0">
            <h4 className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-[0.95rem] tw-font-semibold tw-text-content">
              {p.featured && <Star size={13} className="tw-shrink-0 tw-fill-current tw-text-warning" />}
              <span className="tw-truncate">{p.title}</span>
            </h4>
            {(p.role || range) && (
              <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-content-subtle">
                {[p.role, range].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          {!readOnly && (
            <div className="tw-flex tw-shrink-0 tw-gap-0.5">
              <IconButton label="Edit project" icon={Pencil} size="sm" onClick={onEdit} />
              <IconButton label="Remove project" icon={Trash2} size="sm" variant="ghost-destructive" onClick={onDelete} />
            </div>
          )}
        </div>

        {p.description && (
          <p className="tw-m-0 tw-text-[0.82rem] tw-leading-relaxed tw-text-content-muted">
            {p.description}
          </p>
        )}

        {p.highlights?.length > 0 && (
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
            {p.highlights.map((h, i) => (
              <li key={i} className="tw-flex tw-gap-1.5 tw-text-[0.8rem] tw-leading-snug tw-text-content-muted">
                <span className="tw-mt-1.5 tw-h-1 tw-w-1 tw-shrink-0 tw-rounded-full tw-bg-primary" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        {p.techStack?.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {p.techStack.map((t) => (
              <span key={t} className="tw-rounded tw-bg-surface-hover tw-px-1.5 tw-py-0.5 tw-text-[0.68rem] tw-font-medium tw-text-content-muted">
                {t}
              </span>
            ))}
          </div>
        )}

        {links.length > 0 && (
          <div className="tw-mt-auto tw-flex tw-flex-wrap tw-gap-1.5 tw-pt-1">
            {links.map(({ href, icon, label }) => (
              <NavChip key={label} href={href} icon={icon} shape="chip" size="xs">
                {label}
              </NavChip>
            ))}
          </div>
        )}
      </div>
    </li>
  );
};

/* ── Add / edit ───────────────────────────────────────────────────────────── */

const ProjectModal = ({ mode, project, onClose, onSaved }) => {
  const [form, setForm] = useState(() => (project ? toForm(project) : EMPTY));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Choose an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { setError("Cover images must be under 5 MB."); return; }
    setUploading(true);
    setError("");
    try {
      const { key } = await uploadFile(file);
      set({ coverImage: key });
    } catch (err) {
      setError(errorText(err, "Failed to upload the image."));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError("A project title is required."); return; }
    setSaving(true);
    setError("");
    try {
      const payload = toPayload(form);
      const { portfolio, message } = mode === "add"
        ? await projectsApi.add(payload)
        : await projectsApi.update(project._id, payload);
      onSaved(portfolio, message);
    } catch (err) {
      setError(errorText(err, "Failed to save the project."));
      setSaving(false);
    }
  };

  return (
    <AppModal
      show
      onHide={onClose}
      size="lg"
      title={mode === "add" ? "Add a project" : "Edit project"}
      icon={<FolderGit2 size={18} />}
      footer={
        <div className="tw-flex tw-justify-end tw-gap-2">
          <AppButton variant="secondary" onClick={onClose} disabled={saving}>Cancel</AppButton>
          <AppButton onClick={submit} disabled={saving || uploading}>
            {saving ? "Saving…" : "Save project"}
          </AppButton>
        </div>
      }
    >
      <form className="tw-flex tw-flex-col tw-gap-4" onSubmit={submit}>
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        <FormField label="Project title" required>
          <Input value={form.title} onChange={(e) => set({ title: e.target.value })}
            placeholder="e.g. Real-time attendance dashboard" maxLength={140} autoFocus />
        </FormField>

        <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-2">
          <FormField label="Your role" hint="What you personally did">
            <Input value={form.role} onChange={(e) => set({ role: e.target.value })}
              placeholder="e.g. Backend & deployment" maxLength={120} />
          </FormField>
          <FormField label="Tech stack" hint="Comma separated">
            <Input value={form.techStack} onChange={(e) => set({ techStack: e.target.value })}
              placeholder="React, Node.js, PostgreSQL" />
          </FormField>
        </div>

        <FormField label="Description">
          <Textarea rows={3} value={form.description}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="What problem it solves and how it works." maxLength={4000} />
        </FormField>

        <FormField
          label="Highlights"
          hint="One per line. Numbers beat adjectives — 'cut page load from 4s to 900ms' beats 'improved performance'."
        >
          <Textarea rows={3} value={form.highlights}
            onChange={(e) => set({ highlights: e.target.value })} />
        </FormField>

        <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-3">
          <FormField label="Repository">
            <Input type="url" value={form.repoUrl} onChange={(e) => set({ repoUrl: e.target.value })}
              placeholder="https://github.com/…" />
          </FormField>
          <FormField label="Live demo">
            <Input type="url" value={form.liveUrl} onChange={(e) => set({ liveUrl: e.target.value })}
              placeholder="https://…" />
          </FormField>
          <FormField label="Demo video">
            <Input type="url" value={form.videoUrl} onChange={(e) => set({ videoUrl: e.target.value })}
              placeholder="https://youtube.com/…" />
          </FormField>
        </div>

        <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-2">
          <FormField label="Started">
            <Input type="date" value={form.startDate} onChange={(e) => set({ startDate: e.target.value })} />
          </FormField>
          <FormField label="Finished">
            <Input type="date" value={form.endDate} disabled={form.ongoing}
              onChange={(e) => set({ endDate: e.target.value })} />
          </FormField>
        </div>

        <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2">
          <Checkbox label="Still working on it" checked={form.ongoing}
            onChange={(e) => set({ ongoing: e.target.checked })} />
          <Checkbox label="Feature this project" description="Featured projects appear first"
            checked={form.featured} onChange={(e) => set({ featured: e.target.checked })} />
        </div>

        <FormField label="Cover image" hint="Optional — a screenshot makes a project card far more readable">
          <div className="tw-flex tw-items-center tw-gap-2">
            <input type="file" accept="image/*" onChange={onCover}
              className="tw-text-xs tw-text-content-muted file:tw-mr-2 file:tw-cursor-pointer file:tw-rounded-md file:tw-border-0 file:tw-bg-surface-hover file:tw-px-3 file:tw-py-1.5 file:tw-text-xs file:tw-font-medium file:tw-text-content" />
            {uploading && <Loader2 size={15} className="tw-animate-spin tw-text-primary" />}
            {form.coverImage && !uploading && (
              <span className="tw-text-xs tw-font-medium tw-text-success">Image attached</span>
            )}
          </div>
        </FormField>
      </form>
    </AppModal>
  );
};

export default ProjectsSection;
